import React, { useEffect, useState } from "react";
import { Container, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import { authActions } from "../features/auth/authSlice";
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import useUserSwitcher from "../hooks/useUserSwitcher";
import { UserIcon } from "../lib/icons";
import { capitalize } from "../lib/utils";
import { INavLinkType } from "../types/shared";

export const topBarLinks: INavLinkType[] = [
    {
        title: "History",
        location: "/history",
    },
    {
        title: "Testimonials",
        location: "/testimonials",
    },
    {
        title: "Sponsor",
        location: "/sponsor",
    },
    {
        title: "Wisconsin Baseball Tournaments",
        location: "https://www.wisconsinbaseballtournaments.com/",
        external: true,
    },
    {
        title: "Illinois Baseball Tournaments",
        location: "https://www.illinoisbaseballtournaments.com/",
        external: true,
    },
];

const TopBar = (): React.JSX.Element => {
    const [show, setShow] = useState<boolean>(false);
    const { user } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { handleExitUserSwitch, isSimulatingUser } = useUserSwitcher();

    const isUserSwitched = isSimulatingUser();

    useEffect(() => {
        const nonProdMessage = document.getElementById("nonProdMessage");
        const simUserMessage = document.getElementById("simUserMessage");
        const topBarWrapper = document.getElementById("topBarWrapper");

        if (simUserMessage) {
            topBarWrapper!.style.marginTop = `${simUserMessage.offsetHeight}px`;
        }

        if (nonProdMessage) {
            if (simUserMessage) {
                topBarWrapper!.style.marginTop = `${simUserMessage.offsetHeight + nonProdMessage.offsetHeight}px`;
                simUserMessage!.style.marginTop = `${nonProdMessage.offsetHeight}px`;
            } else {
                topBarWrapper!.style.marginTop = `${nonProdMessage.offsetHeight}px`;
            }
        }
    }, [isUserSwitched]);

    return (
        <>
            {process.env.BUILD_ENV !== "production" && (
                <div
                    id="nonProdMessage"
                    className={process.env.BUILD_ENV}
                    data-testid="nonProdMessage"
                >
                    <p>
                        {capitalize(process.env.BUILD_ENV as string)}{" "}
                        Environment
                    </p>
                </div>
            )}
            {isUserSwitched && (
                <div id="simUserMessage" data-testid="simUserMessage">
                    <p>
                        You are currently logged in as {user?.firstName}{" "}
                        {user?.lastName}. Click{" "}
                        <span
                            onClick={() => handleExitUserSwitch()}
                            style={{
                                cursor: "pointer",
                                textDecoration: "underline",
                            }}
                        >
                            here
                        </span>{" "}
                        to return to your user.
                    </p>
                </div>
            )}
            <div id="topBarWrapper">
                <Navbar
                    expand="md"
                    collapseOnSelect
                    data-bs-theme="dark"
                    className="d-none d-lg-flex"
                >
                    <Container
                        fluid="md"
                        className="topbar"
                        data-testid="topbar"
                    >
                        <Navbar.Toggle aria-controls="basic-navbar-nav" />
                        <Navbar.Collapse>
                            <Nav>
                                {topBarLinks.map((link: INavLinkType) => (
                                    <Nav.Item key={link.title}>
                                        {link.external ? (
                                            <a
                                                href={link.location}
                                                className="nav-link"
                                                role="topNavLink"
                                            >
                                                {link.title}
                                            </a>
                                        ) : (
                                            <NavLink
                                                to={link.location}
                                                className="nav-link"
                                                role="topNavLink"
                                            >
                                                {link.title}
                                            </NavLink>
                                        )}
                                    </Nav.Item>
                                ))}

                                {user ? (
                                    <NavDropdown
                                        key={`profile-dropdown-${show}`}
                                        title={`Hello ${user.firstName}`}
                                        show={show}
                                        onMouseEnter={() => setShow(true)}
                                        onMouseLeave={() => setShow(false)}
                                    >
                                        <NavDropdown.Item
                                            as={Link}
                                            to="/profile"
                                            onClick={() => setShow(false)}
                                        >
                                            <UserIcon className="icon" />
                                            My Profile
                                        </NavDropdown.Item>
                                        <NavDropdown.Item
                                            onClick={() => {
                                                setShow(false);
                                                if (isUserSwitched) {
                                                    handleExitUserSwitch();
                                                } else {
                                                    dispatch(
                                                        authActions.logout(),
                                                    );
                                                }
                                                navigate("/");
                                            }}
                                        >
                                            Logout
                                        </NavDropdown.Item>
                                    </NavDropdown>
                                ) : (
                                    <Nav.Item>
                                        <NavLink
                                            to="/login"
                                            className={"nav-link login"}
                                            role="login-link"
                                        >
                                            <UserIcon className="icon" />
                                            Login
                                        </NavLink>
                                    </Nav.Item>
                                )}
                            </Nav>
                        </Navbar.Collapse>
                    </Container>
                </Navbar>
            </div>
        </>
    );
};

export default TopBar;
